import { Fragment, useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Popover, Transition } from '@headlessui/react'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/solid'
import { ShoppingCart, Heart } from 'lucide-react'
import { useAuth } from '../context/auth-context'
import { ThemeToggle } from './ThemeToggle'
import { useWishlist } from '../context/wishlist-context'

export const Navbar = () => {
  const { user } = useAuth()
  const location = useLocation()
  const displayName = user && user?.firstName + ' ' + user?.lastName
  const [isOpen, setIsOpen] = useState(false)
  const [cartCount, setCartCount] = useState(0)
  const { wishlistCount } = useWishlist()

  useEffect(() => {
    const updateCartCount = () => {
      const cart = localStorage.getItem('cart')
      if (cart) {
        const parsed = JSON.parse(cart)
        setCartCount(parsed.items ? parsed.items.length : 0)
      } else {
        setCartCount(0)
      }
    }

    window.addEventListener('storage', updateCartCount)
    window.addEventListener('cart-changed', updateCartCount) // <-- add this
    updateCartCount()

    return () => {
      window.removeEventListener('storage', updateCartCount)
      window.removeEventListener('cart-changed', updateCartCount) // <-- and this
    }
  }, [])

  const navigation =
    displayName?.length > 0
      ? [
          { name: 'صفحه اصلی', href: '/' },
          { name: 'کتاب های درخواستی', href: '/product-requests' },
          { name: 'سفارش ها', href: '/orders' },
          { name: 'درباره ما', href: '/about' },
          { name: 'تماس با ما', href: '/contact-us' },
        ]
      : [
          { name: 'صفحه اصلی', href: '/' },
          { name: 'کتاب های درخواستی', href: '/product-requests' },
          { name: 'درباره ما', href: '/about' },
          { name: 'تماس با ما', href: '/contact-us' },
        ]

  const closePopover = () => {
    setIsOpen(false)
  }

  const togglePopover = () => {
    setIsOpen((prev) => !prev)
  }

  return (
    <Popover>
      <div
        className='fixed backdrop-blur py-4 top-0 z-40 w-full lg:px-8 text-black'
        style={{ backgroundColor: 'var(--navbar-bg)' }}
      >
        <nav
          className='w-[95%] mx-auto flex-grow space-y-7'
          aria-label='Global'
        >
          <div className='container text-lg font-medium relative flex items-center justify-between sm:h-10'>
            <div className='flex items-center gap-3'>
              <ThemeToggle />
              <div className='hidden lg:flex items-center gap-3 text-right'>
                <Link
                  to='/wishlist'
                  className='relative p-2 text-white hover:bg-hoverColor rounded-md transition-all duration-200'
                >
                  <Heart className='w-6 h-6' />
                  {wishlistCount > 0 && (
                    <span
                      className='absolute -top-1 -right-0 bg-white rounded-full px-2 py-0.5 text-xs font-bold flex items-center justify-center min-w-[20px]'
                      style={{ color: 'var(--accent-primary)' }}
                    >
                      {wishlistCount}
                    </span>
                  )}
                </Link>
                <Link
                  to='/cart'
                  className='relative p-2 text-white hover:bg-hoverColor rounded-md transition-all duration-200'
                >
                  <ShoppingCart className='w-6 h-6' />
                  {cartCount > 0 && (
                    <span
                      className='absolute -top-1 -right-0 bg-white rounded-full px-2 py-0.5 text-xs font-bold flex items-center justify-center min-w-[20px]'
                      style={{ color: 'var(--accent-primary)' }}
                    >
                      {cartCount}
                    </span>
                  )}
                </Link>
                {displayName && displayName?.length > 0 ? (
                  <Link
                    to='/settings'
                    className='font-semibold text-base xl:text-xl text-white hover:bg-hoverColor'
                  >
                    {displayName}
                  </Link>
                ) : (
                  <Link
                    to='/login'
                    className='border border-white rounded-md px-3 py-2 font-semibold text-base xl:text-xl text-white hover:bg-hoverColor'
                  >
                    ورود / ثبت نام
                  </Link>
                )}
              </div>
            </div>

            <div className='flex items-center justify-between'>
              <div className='hidden lg:flex md:items-center lg:flex-row-reverse p-1'>
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href
                  return (
                    <Link
                      to={item.href}
                      key={item.name}
                      className='text-white font-medium text-base xl:text-lg rounded-md px-3 py-2 relative transition-all duration-200'
                      style={{
                        backgroundColor: isActive
                          ? 'var(--navbar-active)'
                          : 'transparent',
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.backgroundColor =
                            'var(--navbar-hover)'
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.backgroundColor = 'transparent'
                        }
                      }}
                    >
                      {item.name}
                    </Link>
                  )
                })}
              </div>
            </div>
            <div className='flex items-center w-full lg:w-auto'>
              <div className='flex w-full items-center justify-between'>
                <Link
                  to='/'
                  className='hover:bg-hoverColor text-white rounded p-2 text-2xl text-left font-medium'
                >
                  Bookstory
                </Link>

                <div className='flex items-center lg:hidden'>
                  <Popover.Button
                    className='inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none'
                    onClick={togglePopover}
                  >
                    <span className='sr-only'>Open main menu</span>
                    <Bars3Icon className='h-6 w-6' aria-hidden='true' />
                  </Popover.Button>
                </div>
              </div>
            </div>
            {/* <div className='flex items-center'>
              <div className='hidden lg:block text-right mr-4'>
                {displayName && displayName?.length > 0 ? (
                  <Link
                    to='/settings'
                    className='font-semibold text-base xl:text-xl text-white hover:bg-hoverColor'
                  >
                    {displayName}
                  </Link>
                ) : (
                  <Link
                    to='/login'
                    className='font-semibold text-base xl:text-xl text-white hover:bg-hoverColor'
                  >
                    ورود / ثبت نام
                  </Link>
                )}
              </div>
            </div> */}
          </div>
        </nav>
      </div>

      {/* Mobile menu overlay */}
      <Transition
        as={Fragment}
        show={isOpen}
        enter='duration-300 ease-out'
        enterFrom='opacity-0'
        enterTo='opacity-100'
        leave='duration-200 ease-in'
        leaveFrom='opacity-100'
        leaveTo='opacity-0'
      >
        <div
          className='fixed inset-0 bg-black/30 z-40 lg:hidden'
          onClick={closePopover}
        />
      </Transition>

      {/* Mobile menu panel */}
      <Transition
        as={Fragment}
        show={isOpen}
        enter='duration-300 ease-out'
        enterFrom='opacity-0 translate-x-full'
        enterTo='opacity-100 translate-x-0'
        leave='duration-200 ease-in'
        leaveFrom='opacity-100 translate-x-0'
        leaveTo='opacity-0 translate-x-full'
      >
        <Popover.Panel
          focus
          className='fixed inset-y-0 right-0 w-full max-w-sm text-white z-50 transform transition lg:hidden shadow-2xl'
          style={{ backgroundColor: 'var(--navbar-bg)' }}
        >
          <div className='flex flex-col h-full'>
            {/* Header */}
            <div className='flex items-center justify-between px-6 py-5 border-b border-white/10'>
              <Link
                to='/'
                className='text-2xl font-bold tracking-wide'
                onClick={closePopover}
              >
                Bookstory
              </Link>
              <Popover.Button
                className='p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all duration-200 focus:outline-none'
                onClick={togglePopover}
              >
                <span className='sr-only'>Close main menu</span>
                <XMarkIcon className='h-6 w-6' aria-hidden='true' />
              </Popover.Button>
            </div>

            {/* User section */}
            <div className='px-6 py-6 border-b border-white/10'>
              {displayName?.length > 0 ? (
                <Link
                  to='/settings'
                  className='flex items-center gap-4 group'
                  onClick={closePopover}
                >
                  <div className='w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-lg font-bold'>
                    {displayName.charAt(0)}
                  </div>
                  <div className='flex-1'>
                    <p className='text-lg font-semibold group-hover:text-white/80 transition-colors'>
                      {displayName}
                    </p>
                    <p className='text-sm text-white/60'>مشاهده پروفایل</p>
                  </div>
                </Link>
              ) : (
                <Link
                  to='/login'
                  className='flex items-center justify-center gap-3 w-full py-3.5 px-6 rounded-xl text-lg font-semibold bg-white/15 hover:bg-white/25 transition-all duration-200 border border-white/20'
                  onClick={closePopover}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    className='h-5 w-5'
                    viewBox='0 0 20 20'
                    fill='currentColor'
                  >
                    <path
                      fillRule='evenodd'
                      d='M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z'
                      clipRule='evenodd'
                    />
                  </svg>
                  ورود / ثبت نام
                </Link>
              )}
            </div>

            {/* Quick actions */}
            <div className='px-6 py-4 border-b border-white/10'>
              <div className='flex items-center justify-center gap-6'>
                <Link
                  to='/wishlist'
                  className='flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-white/10 transition-all duration-200 group'
                  onClick={closePopover}
                >
                  <div className='relative'>
                    <Heart className='w-6 h-6 group-hover:scale-110 transition-transform' />
                    {wishlistCount > 0 && (
                      <span
                        className='absolute -top-2 -right-2 bg-white rounded-full px-2 py-0.5 text-xs font-bold min-w-[20px] text-center'
                        style={{ color: 'var(--accent-primary)' }}
                      >
                        {wishlistCount}
                      </span>
                    )}
                  </div>
                  <span className='text-sm text-white/70'>علاقه‌مندی</span>
                </Link>
                <Link
                  to='/cart'
                  className='flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-white/10 transition-all duration-200 group'
                  onClick={closePopover}
                >
                  <div className='relative'>
                    <ShoppingCart className='w-6 h-6 group-hover:scale-110 transition-transform' />
                    {cartCount > 0 && (
                      <span
                        className='absolute -top-2 -right-2 bg-white rounded-full px-2 py-0.5 text-xs font-bold min-w-[20px] text-center'
                        style={{ color: 'var(--accent-primary)' }}
                      >
                        {cartCount}
                      </span>
                    )}
                  </div>
                  <span className='text-sm text-white/70'>سبد خرید</span>
                </Link>
              </div>
            </div>

            {/* Navigation items */}
            <nav className='flex-1 px-4 py-4 overflow-y-auto'>
              <ul className='space-y-1'>
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href
                  return (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        className={`flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-medium transition-all duration-200 ${
                          isActive
                            ? 'bg-white/20 shadow-lg'
                            : 'hover:bg-white/10'
                        }`}
                        onClick={closePopover}
                      >
                        <span
                          className={`flex-1 text-right ${
                            isActive ? 'text-white' : 'text-white/90'
                          }`}
                        >
                          {item.name}
                        </span>
                        {isActive && (
                          <span className='w-2 h-2 rounded-full bg-white animate-pulse'></span>
                        )}
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </nav>

            {/* Footer */}
            <div className='px-6 py-4 border-t border-white/10'>
              <div className='flex items-center justify-between'>
                <ThemeToggle />
                <p className='text-sm text-white/50'>© 2024 Bookstory</p>
              </div>
            </div>
          </div>
        </Popover.Panel>
      </Transition>
    </Popover>
  )
}

export default Navbar
